import { useEffect, useState } from 'react';
import { api } from '../lib/api.js';
import { useAuth } from '../lib/auth.jsx';
import { formatInTz } from '../lib/utcClock.js';
import { DEFAULT_TIMEZONE } from '../lib/timezones.js';
import { HeaderActions } from '../lib/pageHeader.jsx';
import Pagination, { PAGE_SIZE } from '../components/ui/Pagination.jsx';

function dateToMs(s, endOfDay = false) {
  if (!s) return '';
  const [y, m, d] = s.split('-').map(Number);
  return endOfDay
    ? Date.UTC(y, m - 1, d, 23, 59, 59, 999)
    : Date.UTC(y, m - 1, d, 0, 0, 0, 0);
}

const ACTION_TONE = {
  'auth.login.success':   'bg-emerald-50 text-emerald-700',
  'auth.login.fail':      'bg-red-50 text-red-700',
  'auth.logout':          'bg-ink-100 text-ink-700',
  'user.create':          'bg-sky-50 text-sky-700',
  'user.update':          'bg-amber-50 text-amber-700',
  'user.delete':          'bg-red-50 text-red-700',
  'user.password.change': 'bg-fuchsia-50 text-fuchsia-700',
  'user.password.reset':  'bg-fuchsia-50 text-fuchsia-700',
  'apikey.create':        'bg-sky-50 text-sky-700',
  'apikey.update':        'bg-amber-50 text-amber-700',
  'apikey.regenerate':    'bg-orange-50 text-orange-700',
  'apikey.toggle':        'bg-indigo-50 text-indigo-700',
  'apikey.delete':        'bg-red-50 text-red-700'
};

function ActionChip({ action }) {
  const tone = ACTION_TONE[action] ?? 'bg-ink-100 text-ink-700';
  return (
    <span className={`inline-flex items-center rounded px-1.5 py-0.5 font-mono text-[11px] ${tone}`}>
      {action}
    </span>
  );
}

export default function Logs() {
  const { user } = useAuth();
  const tz = user?.timezone || DEFAULT_TIMEZONE;
  const [items, setItems] = useState(null);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [error, setError] = useState('');
  const [actions, setActions] = useState([]);
  const [filters, setFilters] = useState({ action: '', user_id: '', from: '', to: '' });

  async function load(opts = {}) {
    setError('');
    const params = new URLSearchParams();
    const f = opts.filters ?? filters;
    const off = opts.offset ?? offset;
    if (f.action)  params.set('action', f.action);
    if (f.user_id) params.set('user_id', f.user_id);
    if (f.from)    params.set('from', String(dateToMs(f.from, false)));
    if (f.to)      params.set('to',   String(dateToMs(f.to,   true)));
    params.set('limit',  String(PAGE_SIZE));
    params.set('offset', String(off));
    try {
      const r = await api.get(`/logs?${params.toString()}`);
      setItems(r.items ?? []);
      setTotal(r.total ?? 0);
      setOffset(r.offset ?? 0);
    } catch (e) {
      setError(e.message);
    }
  }

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get('/logs/actions');
        setActions(r.items ?? []);
      } catch { /* ignore */ }
    })();
    load({ offset: 0 });
  }, []);

  function applyFilters(ev) {
    ev.preventDefault();
    load({ offset: 0 });
  }

  function resetFilters() {
    const empty = { action: '', user_id: '', from: '', to: '' };
    setFilters(empty);
    load({ filters: empty, offset: 0 });
  }

  return (
    <div className="space-y-4">
      <HeaderActions>
        <button
          className="btn-ghost"
          onClick={() => load()}
          title="Reload current page with active filters"
        >
          <i className="fa-solid fa-arrows-rotate" />
          <span className="ml-2">Refresh</span>
        </button>
      </HeaderActions>

      <div>
        <p className="text-sm text-ink-500">Admin events and authentication. Timestamps shown in your timezone; filters use UTC days.</p>
      </div>

      <form onSubmit={applyFilters} className="card flex flex-wrap items-end gap-3 p-4">
        <div className="min-w-[180px] flex-1">
          <label className="mb-1 block text-xs font-semibold text-ink-500">Action</label>
          <select
            className="input"
            value={filters.action}
            onChange={(e) => setFilters((f) => ({ ...f, action: e.target.value }))}
          >
            <option value="">any</option>
            {actions.map((a) => <option key={a} value={a}>{a}</option>)}
          </select>
        </div>
        <div className="w-32">
          <label className="mb-1 block text-xs font-semibold text-ink-500">User ID</label>
          <input
            className="input"
            type="number"
            min="1"
            value={filters.user_id}
            onChange={(e) => setFilters((f) => ({ ...f, user_id: e.target.value }))}
          />
        </div>
        <div className="w-44">
          <label className="mb-1 block text-xs font-semibold text-ink-500">From (UTC)</label>
          <input
            className="input"
            type="date"
            value={filters.from}
            onChange={(e) => setFilters((f) => ({ ...f, from: e.target.value }))}
          />
        </div>
        <div className="w-44">
          <label className="mb-1 block text-xs font-semibold text-ink-500">To (UTC)</label>
          <input
            className="input"
            type="date"
            value={filters.to}
            onChange={(e) => setFilters((f) => ({ ...f, to: e.target.value }))}
          />
        </div>
        <div className="flex gap-2">
          <button type="submit" className="btn-primary">Apply</button>
          <button type="button" className="btn-ghost" onClick={resetFilters}>Reset</button>
        </div>
      </form>

      {error && <div className="card p-4 text-sm text-red-700">{error}</div>}

      <div className="card overflow-hidden">
        {items === null ? (
          <div className="p-6 text-sm text-ink-500">Loading…</div>
        ) : items.length === 0 ? (
          <div className="p-8 text-center text-sm text-ink-500">No log entries.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-ink-100 bg-ink-50 text-left text-xs font-semibold uppercase tracking-wider text-ink-500">
                  <th className="px-4 py-2.5">When</th>
                  <th className="px-4 py-2.5">Action</th>
                  <th className="px-4 py-2.5">User</th>
                  <th className="px-4 py-2.5">Target</th>
                  <th className="px-4 py-2.5">Meta</th>
                  <th className="px-4 py-2.5">IP</th>
                </tr>
              </thead>
              <tbody>
                {items.map((row) => (
                  <tr key={row.id} className="border-b border-ink-100 last:border-b-0 align-top">
                    <td className="px-4 py-3 font-mono text-xs text-ink-700 whitespace-nowrap">
                      {formatInTz(row.at, tz)}
                    </td>
                    <td className="px-4 py-3"><ActionChip action={row.action} /></td>
                    <td className="px-4 py-3 text-ink-700">
                      {row.user_username
                        ? <span>{row.user_username} <span className="text-ink-400">#{row.user_id}</span></span>
                        : <em className="text-ink-400">system</em>}
                    </td>
                    <td className="px-4 py-3 font-mono text-xs text-ink-700">
                      {row.target_type
                        ? <>{row.target_type}{row.target_id && <span className="text-ink-400"> #{row.target_id}</span>}</>
                        : <em className="text-ink-400">—</em>}
                    </td>
                    <td className="px-4 py-3 max-w-[320px] font-mono text-[11px] text-ink-600">
                      {row.meta
                        ? <pre className="whitespace-pre-wrap break-words">{typeof row.meta === 'string' ? row.meta : JSON.stringify(row.meta)}</pre>
                        : <em className="text-ink-400">—</em>}
                    </td>
                    <td className="px-4 py-3 font-mono text-xs text-ink-500">{row.ip || <em className="text-ink-400">—</em>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {items && items.length > 0 && (
          <Pagination
            total={total}
            offset={offset}
            limit={PAGE_SIZE}
            onChange={(o) => load({ offset: o })}
          />
        )}
      </div>
    </div>
  );
}
